import React, { useState } from "react";
import { Rocket, ShoppingBag, Sparkles, Trophy, Shield, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

export default function Landing() {
  const { user, loading } = useAuth();
  const [shop, setShop] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleShopifyInstall = (e) => {
    e.preventDefault();
    let s = shop.trim().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!s) return;
    if (!s.endsWith(".myshopify.com")) s = `${s.replace(/\.+$/, "")}.myshopify.com`;
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/shopify/auth?shop=${encodeURIComponent(s)}`;
  };

  const features = [
    { icon: ShoppingBag, title: "Auto-entry tracking", desc: "Shopify orders convert to entries automatically by your custom ratio." },
    { icon: Trophy, title: "Winner picker", desc: "Suspenseful animated draw with verifiable randomness, weighted by entries." },
    { icon: Shield, title: "Fraud detection", desc: "Catch disposable emails, suspicious patterns, and duplicate entrants." },
    { icon: BarChart3, title: "Live leaderboard", desc: "Real-time entrant rankings with referral attribution and bonus entries." },
    { icon: Sparkles, title: "Success Score", desc: "Predict expected entries, revenue, and ROAS before you launch." },
    { icon: Rocket, title: "Compliance built-in", desc: "Official rules generator + AMOE portal so you stay legal across jurisdictions." },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/assets/app-icon-1200.png" alt="SweepLaunch" className="size-7 rounded-md object-cover" />
            <span className="font-display font-bold text-lg tracking-tight">SweepLaunch</span>
          </div>
          <button
            onClick={handleGoogleLogin}
            data-testid="header-signin-button"
            className="text-sm font-medium text-zinc-700 hover:text-zinc-950"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-200 bg-white text-xs text-zinc-600 mb-6">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            Built for Shopify merchants
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-zinc-950">
            Sweepstakes that<br />actually convert.
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-600 max-w-md leading-relaxed">
            The all-in-one giveaway platform for Shopify. Auto-track entries from orders, predict ROAS, and draw winners — without the compliance headache.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <button
              onClick={handleGoogleLogin}
              data-testid="hero-cta-button"
              className="bg-zinc-950 text-white px-6 py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors text-sm"
            >
              Start free with Google
            </button>
            <a href="#features" className="text-sm font-medium text-zinc-700 hover:text-zinc-950">
              See features →
            </a>
          </div>
          <div className="mt-8 flex items-center gap-6 text-xs text-zinc-500">
            <span>No credit card</span>
            <span>·</span>
            <span>Compliance built-in</span>
            <span>·</span>
            <span>Cancel anytime</span>
          </div>

          {/* Install on Shopify */}
          <form onSubmit={handleShopifyInstall} className="mt-10 max-w-md" data-testid="shopify-install-box">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingBag className="size-4 text-emerald-600" strokeWidth={1.5} />
              <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Already on Shopify?</span>
            </div>
            <div className="flex items-stretch gap-2">
              <input
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                placeholder="your-store.myshopify.com"
                data-testid="shopify-install-input"
                className="flex-1 border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none"
              />
              <button
                type="submit"
                data-testid="shopify-install-button"
                className="bg-emerald-600 text-white px-5 py-2.5 rounded-md font-medium hover:bg-emerald-700 transition-colors text-sm whitespace-nowrap"
              >
                Install
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-2">Connect your store and start in seconds — no separate signup.</p>
          </form>
        </div>

        <div className="relative">
          <div className="aspect-video rounded-md overflow-hidden border border-zinc-200 bg-white shadow-sm">
            <img
              src="/assets/ss-dashboard.png"
              alt="SweepLaunch merchant dashboard"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white border border-zinc-200 rounded-md p-4 shadow-sm w-56">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Live entries</div>
            <div className="font-display text-3xl font-bold tabular mt-1">182,500</div>
            <div className="text-xs text-emerald-600 mt-1">+3,210 today</div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20 border-t border-zinc-200">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Everything in one place</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-bold mt-3">
            Stop stitching together five different apps.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 mt-12 border border-zinc-200 rounded-md overflow-hidden">
          {features.map((f) => (
            <div key={f.title} className="bg-white p-6 hover:bg-zinc-50 transition-colors">
              <f.icon className="size-5 text-zinc-950" strokeWidth={1.5} />
              <h3 className="font-display font-semibold text-lg mt-4">{f.title}</h3>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 py-20 border-t border-zinc-200">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Simple pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-bold mt-3">
            Pick a plan, launch in minutes.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-12 items-start">
          {[
            { name: "Starter", price: 29, orders: "250 Orders / Month", giveaways: "1 Active Giveaway", features: ["Auto entry tracking", "CSV exports"] },
            { name: "Pro", price: 79, featured: true, orders: "2,000 Orders / Month", giveaways: "Unlimited Giveaways", features: ["Everything in Starter", "Winner picker", "AMOE portal", "Fraud detection", "Success Score", "Analytics"] },
            { name: "Agency", price: 199, orders: "Unlimited Orders", giveaways: "Unlimited Giveaways", features: ["Everything in Pro", "White-label", "Multi-store", "Priority support", "API access"] },
          ].map((p) => (
            <div
              key={p.name}
              className={`relative rounded-md border bg-white p-6 ${
                p.featured ? "border-zinc-950 ring-2 ring-zinc-950 md:-mt-2 md:pb-8 shadow-lg" : "border-zinc-200"
              }`}
            >
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              <div className="font-display font-bold text-lg">{p.name}</div>
              <div className="font-display text-4xl font-bold mt-3 tabular">
                ${p.price}<span className="text-sm text-zinc-500 font-normal">/mo</span>
              </div>
              <div className={`mt-4 rounded-md px-3 py-2.5 ${p.featured ? "bg-emerald-50" : "bg-zinc-50"}`}>
                <div className={`font-display font-bold text-base ${p.featured ? "text-emerald-700" : "text-zinc-900"}`}>{p.orders}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{p.giveaways}</div>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-zinc-600">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">✓</span> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleGoogleLogin}
                data-testid={`pricing-${p.name.toLowerCase()}-cta`}
                className={`mt-6 w-full py-2.5 rounded-md text-sm font-medium transition-colors ${
                  p.featured
                    ? "bg-emerald-500 text-white hover:bg-emerald-600"
                    : "border border-zinc-200 text-zinc-950 hover:bg-zinc-50"
                }`}
              >
                Get started
              </button>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-10 text-center text-xs text-zinc-500">
        <div className="flex items-center justify-center gap-4 mb-2">
          <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-zinc-950">Privacy Policy</a>
          <span className="text-zinc-300">·</span>
          <a href="/tos" data-testid="footer-tos-link" className="hover:text-zinc-950">Terms of Service</a>
          <span className="text-zinc-300">·</span>
          <a href="mailto:blake@sweeplaunch.com" data-testid="footer-contact-link" className="hover:text-zinc-950">Contact</a>
        </div>
        © {new Date().getFullYear()} SweepLaunch — Built for Shopify merchants.
      </footer>
    </div>
  );
}
